import { defaultTheme } from '@nxsys/constants/defaultConfig';
import styled, { css, keyframes } from 'styled-components';

const { primary } = defaultTheme.theme.palette;

/**
 * The green family comes from the app's own primary scale, not from the design
 * handoff. The handoff (Andy, 13/08) carried its own greens — #0A241D, #10332A,
 * #0F7C63, #4FC49F, #CFEEE1, #125742 — which Varun had sampled off the sales
 * website, so every one of them sat a few degrees away from the product palette.
 * Victor called it on 14/08: follow our primary. Each is now the nearest real
 * token, which is why the shift is small but the palette is no longer a second
 * source of brand green.
 *
 * The neutrals and the status colours below are still the handoff's. They are not
 * brand colours, and the theme has no equivalent for most of them.
 */
export const tfa = {
  page: primary[800], // #00281E
  rail: primary[700], // #00513D
  accent: primary.main, // #00795B
  accentLight: primary[400], // #00CA98 — the same token logo-white-with-name.svg uses
  accentTintBg: primary[75], // #CCF4EA
  accentTintText: primary[700], // #00513D
  surface: '#FCFCFD',
  panel: '#F6F6F8',
  border: '#E7E7EB',
  text: '#2A2D35',
  muted: '#75787F',
  successText: '#1E7A4E',
  successBg: '#DCF3E4',
  errorBorder: '#E4614B',
  errorText: '#C34430',
  warnBg: '#FBF3E0',
  warnBorder: '#EBD9AE',
  warnText: '#5C4A22',
};

/**
 * A token at partial alpha, for focus rings. Derived rather than written out as
 * an rgba() literal: the handoff's greens survived the first palette sweep in
 * exactly this shape. An rgba triple of 15, 124, 99 is #0F7C63 in a costume, and
 * a hex-only grep walked straight past two of them.
 */
export const alpha = (hex: string, a: number): string => {
  const n = Number.parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const rise = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const shake = keyframes`
  0%, 100% { transform: translateX(0); }
  25%      { transform: translateX(-4px); }
  75%      { transform: translateX(4px); }
`;

export const StyledOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  background: ${tfa.page};
  overflow: auto;
`;

export const StyledDialog = styled.div`
  width: 100%;
  max-width: 1000px;
  border-radius: 26px;
  overflow: hidden;
  background: ${tfa.surface};
  box-shadow:
    0 2px 4px rgba(16, 20, 32, 0.2),
    0 48px 90px -40px rgba(16, 20, 32, 0.8);
  display: grid;
  grid-template-columns: 312px 1fr;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledRail = styled.aside`
  background: ${tfa.rail};
  color: #f7f7f8;
  padding: 34px 30px;
  display: flex;
  flex-direction: column;
  gap: 40px;

  .tfa-rail-title {
    /* Load-bearing. GlobalStyle sets a colour on bare h1..h6 (dark slate,
       theme.palette.text.primary), and a declaration applied straight to the
       element beats a colour merely inherited from this rail — so without this
       line the heading renders near-black on the dark green and is unreadable.
       The panel's own h2 rules all set a colour for the same reason; this one
       did not, which is why it was the only heading affected. */
    color: inherit;
    font-size: 24px;
    font-weight: 600;
    letter-spacing: -0.03em;
    line-height: 1.2;
    margin: 0 0 12px;
  }

  .tfa-rail-body {
    font-size: 13.5px;
    line-height: 1.55;
    color: rgba(255, 255, 255, 0.58);
    margin: 0;
  }

  .tfa-rail-foot {
    margin-top: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.45);

    span.dot {
      width: 6px;
      height: 6px;
      border-radius: 999px;
      background: #4bbf8a;
    }
  }

  @media (max-width: 820px) {
    gap: 24px;
    .tfa-rail-foot {
      display: none;
    }
  }
`;

/* The app's own logo, dark-surface variant — the same asset and the same sizing
   AppLogo uses when the sidebar is dark. It carries the whole lockup: the tile,
   its border, the mark and the NXSYS wordmark, all drawn from the primary scale
   (tile primary.50, border primary.200, mark and word primary.400). So there is
   nothing to colour here and nothing to lay out beside it.

   Do not swap this for logo.svg: that file is the favicon glyph, 20x20 and
   icon-only, which is what was wrong with the first attempt. */
export const StyledLogoLockup = styled.div`
  img {
    display: block;
    height: 34px;
    width: auto;
  }
`;

export const StyledStepper = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    display: grid;
    grid-template-columns: 24px 1fr;
    gap: 12px;
  }

  .col {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .bullet {
    width: 24px;
    height: 24px;
    border-radius: 999px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 600;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: rgba(255, 255, 255, 0.45);
    transition: background 0.25s ease;
  }

  li.done .bullet,
  li.current .bullet {
    background: ${tfa.accentLight};
    border-color: ${tfa.accentLight};
    color: ${tfa.rail};
  }

  .line {
    flex: 1;
    width: 1px;
    min-height: 22px;
    margin: 4px 0;
    background: rgba(255, 255, 255, 0.2);
  }

  li.done .line {
    background: ${tfa.accentLight};
  }

  .label {
    font-size: 13.5px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.5);
  }

  li.done .label,
  li.current .label {
    color: #fafafb;
  }

  .note {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.4);
    padding-bottom: 14px;
  }

  @media (max-width: 820px) {
    display: flex;
    gap: 8px;

    li {
      grid-template-columns: 24px;
    }
    .label,
    .note,
    .line {
      display: none;
    }
  }
`;

export const StyledPanel = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 596px;

  @media (max-width: 820px) {
    min-height: 0;
  }
`;

export const StyledStepBody = styled.div`
  padding: 44px 48px 0;
  animation: ${rise} 0.35s ease;

  h2 {
    font-size: 21px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${tfa.text};
    margin: 0 0 6px;
  }

  p.sub {
    font-size: 14px;
    color: ${tfa.muted};
    margin: 0 0 24px;
  }

  @media (max-width: 820px) {
    padding: 28px 24px 0;
  }
`;

export const StyledBlockedNotice = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  border-radius: 13px;
  background: ${tfa.warnBg};
  border: 1px solid ${tfa.warnBorder};
  padding: 14px 16px;
  margin-bottom: 22px;

  .bang {
    flex: none;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    background: #d8a63f;
    color: #4a3a18;
    font-size: 13px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .title {
    font-size: 13.5px;
    font-weight: 600;
    color: ${tfa.warnText};
  }

  .body {
    font-size: 13px;
    color: #6d5a30;
  }
`;

export const StyledMethodCard = styled.button<{ $selected: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 14px;
  padding: 26px 22px;
  border-radius: 14px;
  cursor: pointer;
  transition:
    border-color 0.18s ease,
    box-shadow 0.18s ease,
    background 0.18s ease;

  ${({ $selected }) =>
    $selected
      ? css`
          border: 1.5px solid ${tfa.accent};
          background: #f7fcfa;
          box-shadow: 0 0 0 4px ${alpha(tfa.accent, 0.1)};
          .marks {
            background: ${tfa.accentTintBg};
            color: ${tfa.accentTintText};
          }
        `
      : css`
          border: 1.5px solid ${tfa.border};
          background: #fff;
          .marks {
            background: #f5f5f7;
            color: #6f727a;
          }
        `}

  /* The apps themselves, rather than the abstract glyph that was here (Thang,
     17/08) — the card names Google Authenticator and Microsoft Authenticator in
     its body, so showing their marks is what makes it recognisable at a glance.

     One pill holding all four, not four tiles: four 42px boxes would outweigh
     the title they sit above. Monochrome and inheriting the pill's colour on
     purpose — four brand palettes in a row turns a calm card into a logo
     parade, and it keeps the selected and unselected states a single token
     change each. */
  .marks {
    display: flex;
    align-items: center;
    gap: 14px;
    height: 42px;
    padding: 0 18px;
    border-radius: 14px;
  }

  .marks svg {
    width: 20px;
    height: 20px;
    flex: none;
  }

  .title {
    font-size: 14.5px;
    font-weight: 600;
    color: ${tfa.text};
  }

  .desc {
    font-size: 13px;
    line-height: 1.5;
    max-width: 44ch;
    color: #6f727a;
  }
`;

export const StyledConnectGrid = styled.div`
  display: grid;
  grid-template-columns: 210px 1fr;
  gap: 28px;
  align-items: start;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const StyledQrCard = styled.div`
  padding: 15px;
  border-radius: 20px;
  background: #fff;
  border: 1px solid ${tfa.border};
  box-shadow: 0 14px 34px -24px rgba(40, 44, 58, 0.8);
  line-height: 0;

  img {
    width: 180px;
    height: 180px;
    display: block;
  }
`;

export const StyledInstructions = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;

  li {
    display: flex;
    gap: 10px;
    font-size: 13.5px;
    line-height: 1.45;
    color: ${tfa.text};
  }

  .n {
    flex: none;
    width: 19px;
    height: 19px;
    border-radius: 999px;
    background: #f2f2f4;
    color: #6f727a;
    font-size: 11.5px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const StyledSecretPanel = styled.div`
  margin-top: 22px;
  border: 1px solid ${tfa.border};
  border-radius: 14px;
  overflow: hidden;

  button.head {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    background: none;
    border: 0;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    color: ${tfa.text};
  }

  .body {
    padding: 0 14px 14px;
  }

  .label {
    font-size: 11.5px;
    color: ${tfa.muted};
    margin-bottom: 8px;
  }

  .row {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  code {
    flex: 1;
    font-family:
      'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    letter-spacing: 0.1em;
    background: #f5f5f7;
    border-radius: 9px;
    padding: 10px 12px;
    word-break: break-all;
  }
`;

export const StyledDarkButton = styled.button`
  border: 0;
  border-radius: 9px;
  background: ${tfa.rail};
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 14px;
  cursor: pointer;
  white-space: nowrap;
`;

export const StyledCodeInputRow = styled.div<{ $error: boolean }>`
  display: flex;
  gap: 10px;
  ${({ $error }) =>
    $error &&
    css`
      animation: ${shake} 0.4s ease;
    `}

  input {
    width: 58px;
    height: 66px;
    border-radius: 14px;
    text-align: center;
    font-family:
      'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 25px;
    font-weight: 500;
    color: ${tfa.text};
    background: #fcfcfd;
    border: 1px solid #e9e9ed;
    outline: none;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    &.filled {
      background: #fff;
      border-color: #d3d4d9;
    }

    /* A global rule in the app — "input:hover, input:focus { border-color:
       primary !important; box-shadow: none !important }" — reaches these boxes
       too. It tinted whichever box the pointer happened to rest over, so two of
       the six looked active at once, and it stripped the focus ring that tells
       you where you are typing. Only !important beats !important; the reach is
       exactly these six inputs. */
    &:hover:not(:focus) {
      border-color: #e9e9ed !important;
    }

    &.filled:hover:not(:focus) {
      border-color: #d3d4d9 !important;
    }

    &:focus {
      border-color: ${tfa.accent} !important;
      box-shadow: 0 0 0 4px ${alpha(tfa.accent, 0.14)} !important;
    }

    ${({ $error }) =>
      $error &&
      css`
        border-color: ${tfa.errorBorder};
      `}
  }

  @media (max-width: 640px) {
    gap: 6px;
    input {
      width: 100%;
      height: 58px;
      font-size: 21px;
    }
  }
`;

export const StyledHint = styled.p<{ $error?: boolean }>`
  min-height: 20px;
  margin: 12px 0 0;
  font-size: 13px;
  color: ${({ $error }) => ($error ? tfa.errorText : tfa.muted)};
`;

export const StyledCodesPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 9px 14px;
  padding: 20px 22px;
  border-radius: 16px;
  background: ${tfa.panel};
  border: 1px solid ${tfa.border};

  code {
    font-family:
      'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13.5px;
    letter-spacing: 0.07em;
    color: ${tfa.text};
  }
`;

export const StyledGhostButton = styled.button`
  border: 1.5px solid #e5e5e9;
  background: #fff;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: ${tfa.text};
  padding: 9px 14px;
  cursor: pointer;
`;

export const StyledConfirmRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 20px;
  cursor: pointer;
  font-size: 13.5px;
  color: ${tfa.text};

  .box {
    width: 20px;
    height: 20px;
    border-radius: 6px;
    border: 1px solid #d8d9de;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 12px;
    flex: none;
  }

  input:checked + .box {
    background: ${tfa.rail};
    border-color: ${tfa.rail};
  }

  input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

export const StyledFooter = styled.footer`
  margin-top: auto;
  padding: 20px 48px 24px;
  border-top: 1px solid #f0f0f2;
  background: #fdfdfe;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 820px) {
    padding: 16px 24px 20px;
  }
`;

export const StyledTextButton = styled.button`
  border: 0;
  background: none;
  cursor: pointer;
  font-size: 13.5px;
  font-weight: 500;
  color: #6f727a;
  padding: 0;

  /* Both users of this button disable it — the resend cooldown and the emailed
     new-device code — and a link that still looks and feels clickable while doing
     nothing reads as a broken page. */
  &:disabled {
    cursor: default;
    opacity: 0.55;
  }
`;

export const StyledPrimaryButton = styled.button`
  border: 0;
  border-radius: 12px;
  padding: 13px 26px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: ${tfa.rail};
  cursor: pointer;
  transition: background 0.2s ease;

  &:disabled {
    background: #d8d9de;
    cursor: not-allowed;
  }
`;

/**
 * The verify step is *not* in the enrolment dialog — it renders inside the
 * sign-in card, under the same logo as the password form, because it is step two
 * of signing in rather than a separate task.
 *
 * That card's column is wide (42% of the viewport on desktop, all of it below
 * the `sm` breakpoint), and the six code boxes are a fixed ~400px row. Left to
 * stretch, the heading and the boxes hugged the left edge while the action row
 * spanned the full width — so the Verify button ended up a long way from the
 * field it belongs to. This panel gives the whole step one narrow centred
 * column instead, which is also what makes it read as a deliberate step rather
 * than a half-drawn form.
 */
/**
 * The "Try another way" list. A row per method rather than the centred
 * StyledMethodCard used in the wizard: that one is a single hero choice, and
 * three of them stacked reads as three headlines instead of a list you scan.
 */
export const StyledMethodList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  button {
    display: grid;
    grid-template-columns: 40px 1fr;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 14px 16px;
    /* On the button itself, not inherited from the list: a browser's default
       stylesheet centres text on a button element, and a direct declaration beats
       anything handed down from an ancestor. Centred rows read as three posters
       rather than a list of choices. */
    text-align: left;
    border-radius: 13px;
    border: 1.5px solid ${tfa.border};
    background: #fff;
    cursor: pointer;
    transition:
      border-color 0.18s ease,
      background 0.18s ease,
      box-shadow 0.18s ease;
  }

  button:hover {
    border-color: ${tfa.accent};
    background: #f7fcfa;
  }

  button:focus-visible {
    outline: none;
    border-color: ${tfa.accent};
    box-shadow: 0 0 0 4px ${alpha(tfa.accent, 0.14)};
  }

  .tile {
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 17px;
    background: ${tfa.accentTintBg};
    color: ${tfa.accentTintText};
  }

  /* Both are spans in the markup, so they need making block — otherwise they run
     onto one line and the margin below is silently dropped. */
  .title {
    display: block;
    font-size: 14.5px;
    font-weight: 600;
    color: ${tfa.text};
  }

  .desc {
    display: block;
    font-size: 12.5px;
    line-height: 1.45;
    color: ${tfa.muted};
    margin-top: 2px;
  }

  /* The method already in use is listed, so the screen accounts for every
     option, but it cannot be picked again from here. */
  button[aria-current='true'] {
    border-color: ${tfa.accent};
    background: #f7fcfa;
    cursor: default;
  }
`;

export const StyledVerifyPanel = styled.div`
  width: 100%;
  max-width: 420px;
  margin: 0 auto;
  text-align: center;
  animation: ${rise} 0.35s ease;

  h2 {
    font-size: 21px;
    font-weight: 600;
    letter-spacing: -0.02em;
    color: ${tfa.text};
    margin: 0 0 6px;
  }

  p.sub {
    font-size: 14px;
    line-height: 1.5;
    color: ${tfa.muted};
    margin: 0 0 26px;
  }

  /* The six boxes are a fixed-width row by default. Letting them share the
     column instead means the row ends exactly where the Verify button below it
     does — otherwise the two edges miss each other by ~11px, which is the kind
     of thing you only see once and then cannot stop seeing. */
  .code-wrap {
    display: flex;
  }

  /* The min-width here is load-bearing. A flex item defaults to min-width auto,
     and this row's min-content width is the sum of six inputs' intrinsic widths
     — about 1900px — so without it the row refuses to shrink into the column. */
  .code-wrap > * {
    flex: 1;
    min-width: 0;
  }

  .code-wrap input {
    flex: 1;
    width: auto;
    min-width: 0;
  }

  .ant-input {
    height: 48px;
    border-radius: 12px;
    text-align: center;
    font-family:
      'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 17px;
    letter-spacing: 0.14em;
  }

  /* Modest margin because the hint above already reserves its 20px whether or
     not it has anything to say — that space is what stops the button jumping
     when an error appears, and it doubles as the gap. */
  button.submit {
    width: 100%;
    margin-top: 8px;
  }

  .links {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 18px;
  }

  .links .sep {
    color: #cdced3;
    font-size: 12px;
  }
`;

export const StyledDone = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px;
  animation: ${rise} 0.4s ease;

  .tick {
    width: 62px;
    height: 62px;
    border-radius: 999px;
    background: ${tfa.successBg};
    color: ${tfa.successText};
    font-size: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }

  h2 {
    font-size: 21px;
    font-weight: 600;
    color: ${tfa.text};
    margin: 0 0 8px;
  }

  p {
    font-size: 14px;
    color: ${tfa.muted};
    max-width: 40ch;
    margin: 0 0 26px;
  }

  .actions {
    display: flex;
    gap: 12px;
  }
`;
